import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, doc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { io } from 'socket.io-client';
import QRCode from 'react-qr-code';
import {
  MessageCircle, Smartphone, LogOut, RefreshCw, Upload, Image as ImageIcon,
  Send, Users, Database, Play, CheckCircle2, AlertCircle, X, Download, TerminalSquare, Edit3, Loader2, Trash2, UploadCloud, Type, Search, Filter, UserCheck, UserPlus, Star
} from 'lucide-react';
import { WHATSAPP_BASE_URL } from '../../config/api';

const SOCKET_URL = WHATSAPP_BASE_URL;

// Handle CJS/ESM interop for Vite
const QR = typeof QRCode === 'object' && QRCode.default ? QRCode.default : QRCode;

export default function AdminWhatsApp() {
  const [socket, setSocket] = useState(null);

  // App State
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, qr, connected
  const [qrCode, setQrCode] = useState('');
  const [connectedNumber, setConnectedNumber] = useState('');

  // Contacts State
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);
  const [scrapeKeyword, setScrapeKeyword] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'new' | 'registered' | 'customer'

  // Message State
  const [message, setMessage] = useState('');
  const [media, setMedia] = useState(null); // { base64, mimetype, name }

  // Execution State
  const [isSending, setIsSending] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [scrapeProgress, setScrapeProgress] = useState({ current: 0, total: 0 });
  const [logs, setLogs] = useState([]);
  const logsEndRef = useRef(null);

  // Connect to Socket.io on mount
  useEffect(() => {
    const newSocket = io(SOCKET_URL);
    setSocket(newSocket);

    // Initial status fetch
    fetch(`${SOCKET_URL}/api/status`)
      .then(res => res.json())
      .then(data => {
        if (data.connected) {
          setConnectionStatus('connected');
          setConnectedNumber(data.number);
        } else if (data.hasQR) {
          // Waiting for QR event
        }
      })
      .catch(err => {
        addLog('error', `❌ لم نتمكن من الوصول لسيرفر الواتساب. تأكد من تشغيله عبر الأمر (npm run server).`);
      });

    // Socket Events
    newSocket.on('qr', (qr) => {
      setQrCode(qr);
      setConnectionStatus('qr');
      addLog('info', '📱 تم تحديث رمز QR، يرجى مسحه باستخدام تطبيق واتساب');
    });

    newSocket.on('ready', (data) => {
      setConnectionStatus('connected');
      setConnectedNumber(data.number);
      setQrCode('');
      addLog('success', `✅ تم الاتصال بنجاح بالرقم: ${data.number}`);
    });

    newSocket.on('authenticated', () => {
      addLog('info', '🔐 جاري المصادقة وفتح الجلسة...');
    });

    newSocket.on('auth_failure', (msg) => {
      setConnectionStatus('disconnected');
      addLog('error', `❌ فشل المصادقة: ${msg}`);
    });

    newSocket.on('disconnected', (reason) => {
      setConnectionStatus('disconnected');
      setConnectedNumber('');
      setQrCode('');
      addLog('error', `📴 تم قطع الاتصال: ${reason}`);
    });

    // Execution Events
    newSocket.on('log', (log) => {
      addLog(log.type, log.text);
    });

    newSocket.on('scrape-start', (data) => {
      setIsScraping(true);
      setScrapeProgress({ current: 0, total: data.total });
    });

    newSocket.on('scrape-progress', (data) => {
      setScrapeProgress({ current: data.current, total: data.total });
    });

    newSocket.on('bulk-start', (data) => {
      setIsSending(true);
      setProgress({ current: 0, total: data.total });
    });

    newSocket.on('progress', (data) => {
      setProgress({ current: data.current, total: data.total });
      // Update contact status in the local state
      setContacts(prev => prev.map((c, i) => i === data.index ? { ...c, sendStatus: data.status } : c));
    });

    newSocket.on('bulk-complete', () => {
      setIsSending(false);
      setProgress(prev => ({ ...prev, current: prev.total }));
    });

    return () => newSocket.disconnect();
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = (type, text) => {
    setLogs(prev => [...prev, { id: Date.now() + Math.random(), type, text, time: new Date() }]);
  };

  // ── Helper: Sanitize phone number (strip all non-digits) ──
  const sanitizePhone = (raw) => {
    if (!raw) return '';
    return raw.replace(/@c\.us$/i, '').replace(/\D/g, '');
  };

  // ── Helper: Build dual-collection lookup maps ──
  const buildDbLookup = async () => {
    const [custSnap, regSnap] = await Promise.all([
      getDocs(collection(db, 'customers')),
      getDocs(collection(db, 'registrations'))
    ]);
    const customersSet = new Set();
    const registrationsSet = new Set();
    custSnap.docs.forEach(d => {
      const num = sanitizePhone(d.data().whatsapp);
      if (num) customersSet.add(num);
    });
    regSnap.docs.forEach(d => {
      const num = sanitizePhone(d.data().whatsapp || d.data().phone);
      if (num) registrationsSet.add(num);
    });
    return { customersSet, registrationsSet };
  };

  // ── Helper: Categorize a sanitized number against both collections ──
  // Priority: registered FIRST (registrations), then customer, then new
  // 'registered' = exists in `registrations` collection (المسجلين)
  // 'customer'   = exists in `customers` collection (العملاء)
  // 'new'        = not found in either (name is IGNORED, number-only match)
  const categorizeNumber = (number, { customersSet, registrationsSet }) => {
    const clean = sanitizePhone(number);
    if (registrationsSet.has(clean)) return 'registered';
    if (customersSet.has(clean)) return 'customer';
    return 'new';
  };

  // Load Contacts from Firebase (both collections)
  const loadFirebaseContacts = async () => {
    setIsLoadingContacts(true);
    try {
      const [custSnap, regSnap] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'registrations'))
      ]);

      const custContacts = custSnap.docs.map(d => {
        const data = d.data();
        return {
          id: `cust_${d.id}`,
          name: data.name || 'عميل',
          number: sanitizePhone(data.whatsapp),
          timestamp: data.lastChatDate || data.createdAt?.toMillis() || Date.now(),
          sendStatus: 'pending',
          category: 'customer',
          source: 'database'
        };
      }).filter(c => c.number.length >= 8);

      const regContacts = regSnap.docs.map(d => {
        const data = d.data();
        return {
          id: `reg_${d.id}`,
          name: data.name || 'مسجل',
          number: sanitizePhone(data.whatsapp || data.phone),
          timestamp: data.createdAt?.toMillis() || Date.now(),
          sendStatus: 'pending',
          category: 'registered',
          source: 'database'
        };
      }).filter(c => c.number.length >= 8);

      // Deduplicate: if same number in both, keep the REGISTERED entry (priority)
      const regNumbers = new Set(regContacts.map(c => c.number));
      const uniqueCustContacts = custContacts.filter(c => !regNumbers.has(c.number));
      const allContacts = [...regContacts, ...uniqueCustContacts];

      setContacts(allContacts);
      setSelectedContacts(allContacts.map(c => c.id));
      setSearchTerm('');
      addLog('info', `📥 تم جلب ${custContacts.length} عميل + ${uniqueRegContacts.length} مسجل من قاعدة البيانات`);
    } catch (err) {
      addLog('error', `❌ فشل جلب البيانات: ${err.message}`);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // On component mount, see if we navigated here specifically to message selected targets from AdminCustomers
  useEffect(() => {
    // Load persisted data first
    try {
      const savedMsg = localStorage.getItem('lobab_wa_message');
      if (savedMsg) setMessage(savedMsg);

      const savedMedia = localStorage.getItem('lobab_wa_media');
      if (savedMedia) setMedia(JSON.parse(savedMedia));

      const savedContacts = localStorage.getItem('lobab_wa_contacts');
      if (savedContacts) setContacts(JSON.parse(savedContacts));
    } catch (err) { }

    const checkWaTargets = async () => {
      const waTargets = sessionStorage.getItem('lobab_wa_targets');
      if (waTargets) {
        try {
          const targetIds = JSON.parse(waTargets);
          sessionStorage.removeItem('lobab_wa_targets'); // Consume the payload
          localStorage.removeItem('lobab_wa_contacts'); // clear cached staging to prioritize new payload


          setIsLoadingContacts(true);
          addLog('wait', 'جاري تجهيز الأرقام المحددة من قاعدة العملاء للرسائل...');

          const snap = await getDocs(collection(db, 'customers'));
          const fbContacts = snap.docs
            .filter(doc => targetIds.includes(doc.id))
            .map(doc => ({
              id: doc.id,
              name: doc.data().name || 'عميل',
              number: doc.data().whatsapp || '',
              timestamp: doc.data().lastChatDate || doc.data().createdAt?.toMillis() || Date.now(),
              sendStatus: 'pending',
              category: 'registered'
            })).filter(c => c.number.length >= 8);

          setContacts(fbContacts);
          setSelectedContacts(fbContacts.map(c => c.id));
          addLog('success', `📥 تم جلب ${fbContacts.length} عميل محدد لجاهزية الإرسال`);
        } catch (err) {
          addLog('error', `❌ فشل تحليل المحولين: ${err.message}`);
        } finally {
          setIsLoadingContacts(false);
        }
      }
    };
    checkWaTargets();
  }, []);

  // Sync state to LocalStorage
  useEffect(() => {
    localStorage.setItem('lobab_wa_message', message);
  }, [message]);

  useEffect(() => {
    if (media) localStorage.setItem('lobab_wa_media', JSON.stringify(media));
    else localStorage.removeItem('lobab_wa_media');
  }, [media]);

  useEffect(() => {
    if (contacts.length > 0) localStorage.setItem('lobab_wa_contacts', JSON.stringify(contacts));
    else localStorage.removeItem('lobab_wa_contacts');
  }, [contacts]);

  // Scrape Chats from WhatsApp
  const handleScrapeChats = async () => {
    if (connectionStatus !== 'connected') {
      alert('يجب ربط الواتساب أولاً.'); return;
    }

    setIsLoadingContacts(true);
    try {
      const keywordParam = scrapeKeyword.trim() ? `?keyword=${encodeURIComponent(scrapeKeyword.trim())}` : '';
      const res = await fetch(`${SOCKET_URL}/api/scrape-chats${keywordParam}`);
      const data = await res.json();

      // Fetch existing DB to cross-reference with category logic
      const dbMap = await buildDbLookup();

      // Automatically load into frontend table (staging area)
      const mapped = data.contacts.map((c, i) => {
        const cleanNumber = sanitizePhone(c.number);
        return {
          id: `scraped_${i}`,
          name: c.name,
          number: cleanNumber,
          timestamp: c.timestamp,
          lastMessage: c.lastMessage || '',
          sendStatus: 'pending',
          category: categorizeNumber(cleanNumber, dbMap),
          source: 'whatsapp-scraper'
        };
      });
      setContacts(mapped);
      // Select ALL contacts for messaging (universal selection)
      setSelectedContacts(mapped.map(c => c.id));
      setSearchTerm('');
      const newCount = mapped.filter(c => c.category === 'new').length;
      const regCount = mapped.filter(c => c.category === 'registered').length;
      const custCount = mapped.filter(c => c.category === 'customer').length;
      addLog('success', `✅ تم جلب ${mapped.length} جهة اتصال — ${newCount} جديد، ${regCount} مسجل، ${custCount} عميل`);

    } catch (err) {
      addLog('error', `❌ خطأ في سحب المحادثات: ${err.message}`);
    } finally {
      setIsLoadingContacts(false);
      setIsScraping(false);
      setScrapeProgress({ current: 0, total: 0 });
    }
  };

  // Import CSV target contacts
  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    addLog('wait', 'جاري تحليل وقراءة الأرقام من ملف الـ CSV...');
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = text.split('\n').filter(r => r.trim().length > 0);
        if (rows.length < 1) throw new Error('الملف فارغ');

        const parseRow = (str) => {
          const arr = []; let quote = false, col = '';
          for (let i = 0; i < str.length; i++) {
            let cc = str[i];
            if (cc === '"' && quote && str[i + 1] === '"') { col += '"'; i++; continue; }
            if (cc === '"') { quote = !quote; continue; }
            if (cc === ',' && !quote) { arr.push(col); col = ''; continue; }
            col += cc;
          }
          arr.push(col);
          return arr.map(c => c.trim().replace(/^["\u200E\uFEFF]+|["\u200E\uFEFF]+$/g, ''));
        };

        const headers = parseRow(rows[0]).map(h => h.toLowerCase());
        let nameIndex = headers.findIndex(h => h.includes('اسم') || h.includes('name'));
        let phoneIndex = headers.findIndex(h => h.includes('رقم') || h.includes('واتساب') || h.includes('phone') || h.includes('number'));

        const hasHeaders = nameIndex !== -1 && phoneIndex !== -1;
        if (!hasHeaders) {
          nameIndex = 0;
          phoneIndex = headers.length > 1 ? 1 : 0;
        }

        const startIndex = hasHeaders ? 1 : 0;
        const newContacts = [];

        // Fetch existing logic with categories
        const dbMap = await buildDbLookup();

        for (let i = startIndex; i < rows.length; i++) {
          const cols = parseRow(rows[i]);
          let name = cols[nameIndex] || 'عميل مستورد';
          let phoneRaw = cols[phoneIndex] || '';
          let phone = phoneRaw.replace(/\D/g, '');

          if (phone.length >= 8) {
            newContacts.push({
              id: `scraped_${Date.now()}_${i}`,
              name: name,
              number: phone,
              timestamp: Date.now(),
              lastMessage: 'مستورد يدوياً من CSV للمراسلة',
              sendStatus: 'pending',
              category: categorizeNumber(phone, dbMap),
              source: 'csv'
            });
          }
        }

        if (newContacts.length > 0) {
          setContacts(prev => [...prev, ...newContacts]);
          // Select ALL imported contacts for messaging
          const allNewIds = newContacts.map(c => c.id);
          setSelectedContacts(prev => [...prev, ...allNewIds]);
          addLog('success', `🎉 تم رفع وفرز ${newContacts.length} رقم من ملف الـ CSV بنجاح.`);
        } else {
          addLog('error', '❌ لم يتم العثور على أرقام صالحة في الملف!');
        }
      } catch (err) {
        addLog('error', `❌ حدث خطأ أثناء الفتح: ${err.message}`);
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  // Save NEW contacts to Firebase (auto-filters: only saves contacts with category 'new')
  const handleSaveNewToFirebase = async () => {
    const newContacts = contacts.filter(c => c.category === 'new' && c.id.startsWith('scraped_'));
    if (newContacts.length === 0) {
      alert('لا يوجد جهات اتصال جديدة لحفظها. جميع جهات الاتصال موجودة مسبقاً في قاعدة البيانات.'); return;
    }

    const skippedCount = contacts.filter(c => c.category !== 'new').length;
    setIsSaving(true);
    addLog('info', `💾 جاري حفظ ${newContacts.length} جهة اتصال جديدة (تم تخطي ${skippedCount} جهة موجودة مسبقاً)...`);
    try {
      const batch = writeBatch(db);
      newContacts.forEach(c => {
        const ref = doc(collection(db, 'customers'));
        batch.set(ref, {
          name: c.name,
          whatsapp: c.number,
          email: '',
          level: 'beginner',
          source: c.source || 'whatsapp-scraper',
          notes: c.lastMessage || 'تم سحبه من الواتساب',
          status: 'interested',
          amountPaid: 0,
          lastChatDate: c.timestamp || Date.now(),
          createdAt: serverTimestamp()
        });
      });
      await batch.commit();

      // Mark saved contacts as 'registered' instead of removing them
      setContacts(prev => prev.map(c =>
        newContacts.find(nc => nc.id === c.id) ? { ...c, category: 'registered' } : c
      ));

      addLog('success', `✅ تم حفظ ${newContacts.length} جهة اتصال جديدة بنجاح! تم تحديث حالتهم إلى "مسجل".`);
    } catch (err) {
      addLog('error', `❌ خطأ في الحفظ: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedContacts.length === 0) return;

    if (!confirm(`هل أنت متأكد من إزالة ${selectedContacts.length} جهة اتصال من قائمة المستهدفين الحالية؟ (لن يتم حذفهم من قاعدة بيانات العملاء الأساسية)`)) {
      return;
    }

    const remaining = contacts.filter(c => !selectedContacts.includes(c.id));
    setContacts(remaining);
    setSelectedContacts([]);
    addLog('info', `🗑️ تم إزالة جهات الاتصال المحددة من النافذة بنجاح.`);
  };

  // Handle Image Upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64Data = ev.target.result.split(',')[1];
      setMedia({
        base64: base64Data,
        mimetype: file.type,
        name: file.name,
        preview: ev.target.result
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveMedia = () => {
    setMedia(null);
  };

  // Start Bulk Send
  const handleStopSending = async () => {
    setIsSending(false);
    try {
      await fetch(`${SOCKET_URL}/api/stop-send`, { method: 'POST' });
    } catch (e) {
      addLog('error', '❌ فشل الاتصال بالسيرفر لإيقاف الحملة.');
    }
  };

  const startBulkSend = async () => {
    if (connectionStatus !== 'connected') {
      alert('يجب ربط الواتساب أولاً.'); return;
    }
    const toSend = contacts.filter(c => selectedContacts.includes(c.id));

    if (toSend.length === 0) {
      alert('الرجاء تحديد جهة اتصال واحدة على الأقل.'); return;
    }
    if (!message.trim() && !media) {
      alert('الرجاء إدخال رسالة أو إرفاق صورة.'); return;
    }
    if (!confirm(`سيتم إرسال الرسالة إلى ${toSend.length} جهة اتصال. المدة المتوقعة: ~${(toSend.length * 11 / 60).toFixed(1)} دقيقة.\n\nهل أنت متأكد من الاستمرار؟`)) {
      return;
    }

    // Reset status of selected
    setContacts(prev => prev.map(c => selectedContacts.includes(c.id) ? { ...c, sendStatus: 'pending' } : c));

    try {
      const res = await fetch(`${SOCKET_URL}/api/send-bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacts: toSend,
          message: message,
          image: media ? { base64: media.base64, mimetype: media.mimetype } : null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

    } catch (err) {
      addLog('error', `❌ فشل بدء العملية: ${err.message}`);
    }
  };

  // Manual Logout
  const handleLogout = async () => {
    try {
      await fetch(`${SOCKET_URL}/api/logout`, { method: 'POST' });
    } catch (e) {
      addLog('error', 'فشل تسجيل الخروج، تأكد من تشغيل السيرفر.');
    }
  };

  // Toggle Contact Selection
  const toggleContact = (id) => {
    setSelectedContacts(prev =>
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
    );
  };

  // ── Filtered contacts: search + category filter ──
  const filteredContacts = contacts.filter(c => {
    const matchesSearch = (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.number && c.number.includes(searchTerm));
    const matchesCategory = activeFilter === 'all' || c.category === activeFilter;
    return matchesSearch && matchesCategory;
  });

  // Smart Selection: only affects VISIBLE (filtered) rows
  const handleSelectVisible = () => {
    const visibleIds = filteredContacts.map(c => c.id);
    setSelectedContacts(prev => Array.from(new Set([...prev, ...visibleIds])));
  };

  const handleDeselectVisible = () => {
    const visibleIds = new Set(filteredContacts.map(c => c.id));
    setSelectedContacts(prev => prev.filter(id => !visibleIds.has(id)));
  };

  // Keep toggleAllContacts as a fallback for the checkbox if needed
  const toggleAllContacts = () => {
    const visibleIds = filteredContacts.map(c => c.id);
    const allVisible = visibleIds.length > 0 && visibleIds.every(id => selectedContacts.includes(id));
    if (allVisible) {
      handleDeselectVisible();
    } else {
      handleSelectVisible();
    }
  };

  const hasNewContacts = contacts.some(c => c.category === 'new' && c.id.startsWith('scraped_'));
  const newContactsCount = contacts.filter(c => c.category === 'new' && c.id.startsWith('scraped_')).length;

  // ── Category counters (from search-filtered, before category filter) ──
  const searchFiltered = contacts.filter(c =>
    (c.name && c.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.number && c.number.includes(searchTerm))
  );
  const catCounts = {
    all: searchFiltered.length,
    new: searchFiltered.filter(c => c.category === 'new').length,
    registered: searchFiltered.filter(c => c.category === 'registered').length,
    customer: searchFiltered.filter(c => c.category === 'customer').length,
  };

  // ── Stats bar ──────────────────────────────────────────────────────────────
  const totalSelected = selectedContacts.length;
  const totalContacts = contacts.length;
  const sentCount = contacts.filter(c => c.sendStatus === 'sent').length;
  const failedCount = contacts.filter(c => c.sendStatus === 'failed').length;
  const sendPct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
  const scrapePct = scrapeProgress.total ? Math.round((scrapeProgress.current / scrapeProgress.total) * 100) : 0;

  // Status badge helper
  const statusBadge = () => {
    if (connectionStatus === 'connected') return { label: 'متصل', dot: 'bg-emerald-400 animate-pulse', pill: 'bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30' };
    if (connectionStatus === 'qr') return { label: 'في انتظار المسح', dot: 'bg-amber-400 animate-ping', pill: 'bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30' };
    return { label: 'مفصول', dot: 'bg-slate-500', pill: 'bg-slate-500/10 text-slate-400 ring-1 ring-slate-500/20' };
  };
  const { label: sLabel, dot: sDot, pill: sPill } = statusBadge();

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-[1400px] mx-auto space-y-6 pb-12 pt-6 px-4 sm:px-6">

      {/* ── Page Header & Quick Stats ───────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 bg-white dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/60 p-6 rounded-2xl shadow-sm">

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-lg bg-slate-50 dark:bg-zinc-800 flex items-center justify-center border border-slate-200 dark:border-zinc-700/50 shrink-0">
            <MessageCircle className="w-6 h-6 text-slate-900 dark:text-white" strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                أتمتة النظام
              </h1>
              <span className="px-2 py-0.5 rounded-lg bg-primary-500 dark:bg-zinc-800 text-white dark:text-zinc-400 text-md font-b uppercase tracking-widest border border-slate-200 dark:border-zinc-700">
                PRO
              </span>
            </div>
            <p className="text-slate-500 dark:text-zinc-400 text-md font-bold">
              التحكم الشامل بجهات الإتصال وحملات المراسلة
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {[
            { icon: Users, value: totalContacts, label: 'إجمالي القائمة', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/10' },
            { icon: CheckCircle2, value: sentCount, label: 'تم الإرسال', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/10' },
            { icon: AlertCircle, value: failedCount, label: 'فشل الإرسال', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/10' },
          ].map(({ icon: Icon, value, label, color, bg }) => (
            <div key={label} className="flex-1 md:flex-none min-w-[130px] p-4 bg-slate-50 dark:bg-zinc-800/40 border border-slate-200 dark:border-zinc-800 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white leading-none tracking-tight">{value}</p>
                  <p className="text-md font-semibold text-slate-500 dark:text-zinc-500 mt-2">{label}</p>
                </div>
                <div className={`p-2 rounded-lg ${bg}`}>
                  <Icon className={`w-6 h-6 ${color}`} strokeWidth={2} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Dashboard Grid ── */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 relative z-10 w-full">

        {/* ══ Left Column: Setup & Setup ══ */}
        <div className="xl:col-span-4 flex flex-col gap-8">

          {/* ── Connection Card ── */}
          <div className="bg-white dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/60 rounded-2xl shadow-sm overflow-hidden flex flex-col">
            <div className="p-5 px-6 border-b border-slate-100 dark:border-zinc-800/60 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${connectionStatus === 'connected' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-50 dark:bg-zinc-800 text-slate-500'}`}>
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-slate-900 dark:text-white">بوابة الاتصال</h3>
                  <p className="text-[13px] text-slate-500 dark:text-zinc-500 mt-0.5">حالة الربط مع الواتساب</p>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider ${connectionStatus === 'connected' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20' :
                connectionStatus === 'qr' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-500/20' :
                  'bg-slate-50 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 border border-slate-200 dark:border-zinc-700'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${connectionStatus === 'connected' ? 'bg-emerald-500' : 'bg-slate-400'} ${connectionStatus !== 'disconnected' ? 'animate-ping' : ''}`} />
                {sLabel}
              </div>
            </div>

            <div className="p-6 flex flex-col items-center justify-center min-h-[300px] flex-1">
              {connectionStatus === 'disconnected' && (
                <div className="text-center space-y-5 animate-in fade-in zoom-in-95 duration-500 w-full">
                  <div className="w-20 h-20 rounded-2xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 flex items-center justify-center mx-auto text-slate-400">
                    <RefreshCw className="w-8 h-8 animate-spin-slow" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white text-lg">بانتظار السيرفر</h4>
                    <p className="text-mdtext-slate-500 dark:text-zinc-400 mt-2 max-w-[220px] mx-auto leading-relaxed">
                      يرجى التأكد من تشغيل خادم المزامنة المحلي في الخلفية
                    </p>
                  </div>
                  <div className="inline-flex items-center gap-2 text-[13px] font-mono font-semibold text-slate-700 dark:text-zinc-300 bg-slate-100 dark:bg-zinc-800 px-4 py-2 rounded-lg user-select-all">
                    npm run server
                  </div>
                </div>
              )}

              {connectionStatus === 'qr' && (
                <div className="flex flex-col items-center gap-6 animate-in zoom-in-95 duration-500 w-full">
                  <div className="p-4 bg-white dark:bg-zinc-100 rounded-2xl shadow-sm border border-slate-100">
                    {qrCode && <QR value={qrCode} size={180} level="H" />}
                  </div>

                  <div className="w-full text-center">
                    <p className="text-mdfont-medium text-slate-600 dark:text-zinc-400">
                      افتح واتساب &gt; الأجهزة المرتبطة &gt; مسح QR
                    </p>
                  </div>
                </div>
              )}

              {connectionStatus === 'connected' && (
                <div className="flex flex-col items-center text-center gap-6 animate-in fade-in slide-in-from-top-4 duration-500 w-full">
                  <div className="w-24 h-24 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-100 dark:border-emerald-500/20">
                    <CheckCircle2 className="w-10 h-10" strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-1">متصل بنجاح</h4>
                    <p className="text-slate-500 dark:text-zinc-400 font-mono text-mdtracking-widest" dir="ltr">
                      +{connectedNumber}
                    </p>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="w-full max-w-[200px] flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 font-bold text-[15px] border border-slate-200 dark:border-zinc-700 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    تسجيل خروج
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Message Composer ── */}
          <div className="bg-white dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/60 rounded-2xl shadow-sm flex flex-col">
            <div className="p-5 px-6 border-b border-slate-100 dark:border-zinc-800/60 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center text-primary-600 dark:text-primary-400">
                <Edit3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-base text-slate-900 dark:text-white">صياغة الحملة</h3>
                <p className="text-[13px] text-slate-500 dark:text-zinc-500 mt-0.5">المرفقات والمحتوى النصي</p>
              </div>
            </div>

            <div className="p-6 flex flex-col gap-6 flex-1">
              {/* Media Upload Area */}
              <div className="relative">
                {media ? (
                  <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-zinc-800 group shadow-sm">
                    <img src={media.preview} alt="preview" className="w-full h-[180px] object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button
                        onClick={handleRemoveMedia}
                        className="bg-white text-red-600 px-4 py-2 rounded-xl flex items-center gap-2 text-[15px] font-bold shadow-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" /> إزالة الصورة
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-slate-200 dark:border-zinc-700/60 rounded-2xl p-8 flex flex-col items-center justify-center bg-slate-50 dark:bg-zinc-800/40 hover:bg-slate-100 dark:hover:bg-zinc-800/80 transition-colors cursor-pointer min-h-[140px]">
                    <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                    <div className="w-12 h-12 rounded-lg bg-white dark:bg-zinc-900 flex items-center justify-center shadow-sm mb-3 border border-slate-200 dark:border-zinc-800 text-slate-400">
                      <UploadCloud className="w-6 h-6" />
                    </div>
                    <p className="text-[15px] font-bold text-slate-700 dark:text-zinc-300">رفع صورة مرفقة</p>
                    <p className="text-md text-slate-500 dark:text-zinc-500 mt-1">اختياري (JPG, PNG)</p>
                  </label>
                )}
              </div>

              {/* Message Textarea */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-md font-bold text-slate-500 dark:text-zinc-400 uppercase tracking-widest px-1">
                  <Type className="w-3.5 h-3.5" /> نص الرسالة
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={6}
                    className="w-full px-5 py-4 bg-slate-50 dark:bg-zinc-900/50 border border-slate-200 dark:border-zinc-800 rounded-xl outline-none focus:border-primary-500 focus:bg-white dark:focus:bg-zinc-900 transition-all text-[15px] text-slate-800 dark:text-zinc-200 resize-none placeholder:text-slate-400 shadow-inner"
                    placeholder="اكتب رسالتك الذكية هنا..."
                  />
                  <div className="absolute left-3 bottom-3">
                    <span className={`text-md font-bold px-2.5 py-1 rounded-md border ${message.length > 500 ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-white dark:bg-zinc-800 text-slate-400 border-slate-200 dark:border-zinc-700'}`}>
                      {message.length} حرف
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ══ Right Column: Target Audience & Executive Console (7 cols) ══ */}
        <div className="xl:col-span-7 flex flex-col gap-8">

          {/* ── Contacts Area ── */}
          {/* ── Contacts Area ── */}
          <div className="bg-white dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800/60 rounded-2xl shadow-sm flex flex-col overflow-hidden relative" style={{ height: '620px' }}>

            {/* Toolbar Header */}
            <div className="p-5 border-b border-slate-100 dark:border-zinc-800/60 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Database className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">قاعدة الاستهداف</h2>
                    <p className="text-md text-slate-500 dark:text-zinc-500 mt-0.5">إدارة وضبط المستفيدين من الحملة</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <input type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} className="hidden" />
                  <button
                    onClick={loadFirebaseContacts}
                    disabled={isLoadingContacts}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-[13px] font-bold border border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-700/50 transition-colors disabled:opacity-50"
                  >
                    <Database className="w-4 h-4 text-primary-500" />
                    جلب من قاعدة البيانات
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoadingContacts || isImporting}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-[13px] font-bold border border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-700/50 transition-colors disabled:opacity-50"
                  >
                    {isImporting ? <Loader2 className="w-4 h-4 animate-spin text-blue-500" /> : <UploadCloud className="w-4 h-4 text-blue-500" />}
                    استيراد CSV
                  </button>
                  <div className="relative">
                    <span className="absolute inset-y-0 right-2.5 flex items-center pointer-events-none">
                      <Search className="w-3.5 h-3.5 text-slate-400" />
                    </span>
                    <input
                      type="text"
                      placeholder="كلمة مفتاحية (مثال: عرض، مهتم)..."
                      value={scrapeKeyword}
                      onChange={(e) => setScrapeKeyword(e.target.value)}
                      className="w-[220px] pr-8 pl-3 py-2.5 text-[13px] bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all font-medium text-slate-800 dark:text-zinc-200 placeholder:text-slate-400 dark:placeholder:text-zinc-500"
                    />
                  </div>
                  <button
                    onClick={handleScrapeChats}
                    disabled={isLoadingContacts || connectionStatus !== 'connected'}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-[13px] font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${isScraping ? 'animate-spin' : ''}`} />
                    سحب المحادثات
                  </button>
                </div>
              </div>

              {/* Sub-toolbar (Search, Quick Select & Actions) */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col md:flex-row items-stretch gap-3">
                  <div className="flex-1 relative">
                    <span className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-slate-400" />
                    </span>
                    <input
                      type="text"
                      placeholder="ابحث بالاسم أو رقم الهاتف..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pr-10 pl-4 py-2.5 text-mdbg-slate-50 dark:bg-zinc-900/50 border border-slate-200 dark:border-zinc-800/80 rounded-lg outline-none focus:border-primary-500 focus:bg-white dark:focus:bg-zinc-900 transition-colors font-medium text-slate-800 dark:text-zinc-200"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    {hasNewContacts && (
                      <button
                        onClick={handleSaveNewToFirebase}
                        disabled={isSaving || newContactsCount === 0}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-bold shadow-sm transition-colors disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
                        حفظ الجدد ({newContactsCount})
                      </button>
                    )}
                    {selectedContacts.length > 0 && (
                      <button
                        onClick={handleDeleteSelected}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-600 hover:bg-red-100 dark:hover:bg-red-500/20 text-[13px] font-bold border border-red-100 dark:border-red-500/20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> استبعاد ({selectedContacts.length})
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Tabs + Counters */}
                {searchFiltered.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Filter className="w-3.5 h-3.5 text-slate-400 ml-1" />
                      <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-500 uppercase tracking-wider">تصفية:</span>
                      {[
                        { key: 'all', label: 'الكل', count: catCounts.all, icon: Users, colors: 'bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-700', activeColors: 'bg-slate-700 dark:bg-zinc-200 text-white dark:text-zinc-900 border-slate-700 dark:border-zinc-200' },
                        { key: 'registered', label: 'المسجلين', count: catCounts.registered, icon: UserCheck, colors: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20', activeColors: 'bg-amber-500 text-white border-amber-600' },
                        { key: 'customer', label: 'العملاء', count: catCounts.customer, icon: Star, colors: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20', activeColors: 'bg-blue-500 text-white border-blue-600' },
                        { key: 'new', label: 'الجدد', count: catCounts.new, icon: UserPlus, colors: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20', activeColors: 'bg-emerald-500 text-white border-emerald-600' },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setActiveFilter(tab.key)}
                          className={`px-3 py-1.5 rounded-md text-[11px] font-bold border transition-all duration-200 ${activeFilter === tab.key ? tab.activeColors : `${tab.colors} hover:opacity-80`}`}
                        >
                          <span className="flex items-center gap-1.5">
                            <tab.icon className="w-3 h-3" />
                            {tab.label} ({tab.count})
                          </span>
                        </button>
                      ))}

                      <div className="w-px h-5 bg-slate-200 dark:bg-zinc-700 mx-1" />

                      <button
                        onClick={handleSelectVisible}
                        className="px-3 py-1.5 rounded-md text-[11px] font-bold bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3" /> تحديد الظاهر</span>
                      </button>
                      <button
                        onClick={handleDeselectVisible}
                        className="px-3 py-1.5 rounded-md text-[11px] font-bold bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <span className="flex items-center gap-1.5"><X className="w-3 h-3" /> إلغاء التحديد</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[12px] font-bold text-slate-500 dark:text-zinc-400 px-2.5 py-1 bg-slate-50 dark:bg-zinc-800/60 rounded-md border border-slate-200 dark:border-zinc-700">
                        الظاهر: <span className="text-primary-600 dark:text-primary-400">{filteredContacts.length}</span>
                      </span>
                      <span className="text-[12px] font-bold text-slate-500 dark:text-zinc-400 px-2.5 py-1 bg-slate-50 dark:bg-zinc-800/60 rounded-md border border-slate-200 dark:border-zinc-700">
                        محدد للإرسال: <span className="text-primary-600 dark:text-primary-400">{totalSelected}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {isScraping && (
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 p-3 rounded-lg flex items-center justify-between">
                  <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> جاري سحب الواتساب...
                  </span>
                  <span className="text-[13px] font-bold text-blue-600 dark:text-blue-400">{scrapePct}%</span>
                </div>
              )}
            </div>

            {/* Contacts Layout body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
              {isLoadingContacts ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                  <p className="text-mdfont-semibold text-slate-500 dark:text-zinc-400">مزامنة البيانات السحابية...</p>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center p-6">
                  <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-zinc-800 flex items-center justify-center text-slate-300">
                    <Users className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-[15px] font-bold text-slate-800 dark:text-zinc-200 mb-1">القائمة فارغة</p>
                    <p className="text-[13px] text-slate-500 dark:text-zinc-500">قم بجلب العملاء، استيراد csv، أو سحب المحادثات</p>
                  </div>
                  <button
                    onClick={loadFirebaseContacts}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white dark:bg-zinc-800 border border-slate-200 dark:border-zinc-700 text-slate-700 dark:text-zinc-300 font-bold text-[13px] hover:bg-slate-50 shadow-sm transition-colors"
                  >
                    <Database className="w-4 h-4 text-primary-500" /> جلب الكل
                  </button>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div
                    onClick={toggleAllContacts}
                    className="flex items-center gap-3 px-5 py-3 bg-slate-50 dark:bg-zinc-900/50 border-b border-slate-100 dark:border-zinc-800/60 cursor-pointer hover:bg-slate-100 dark:hover:bg-zinc-800/60 transition-colors"
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${filteredContacts.length > 0 && filteredContacts.every(c => selectedContacts.includes(c.id))
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800'
                      }`}>
                      {filteredContacts.length > 0 && filteredContacts.every(c => selectedContacts.includes(c.id)) && <CheckCircle2 className="w-3 h-3" />}
                    </div>
                    <span className="text-[13px] font-bold text-slate-600 dark:text-zinc-400">تحديد الظاهر ({filteredContacts.length})</span>
                  </div>

                  <div className="p-3 space-y-1.5">
                    {filteredContacts.map((contact) => (
                      <div
                        key={contact.id}
                        onClick={() => toggleContact(contact.id)}
                        className={`group relative flex items-center justify-between p-3 rounded-xl transition-colors duration-200 cursor-pointer border ${selectedContacts.includes(contact.id)
                          ? 'bg-blue-50 dark:bg-blue-500/5 border-blue-100 dark:border-blue-500/20'
                          : 'bg-white dark:bg-zinc-900/40 border-slate-100 dark:border-zinc-800/50 hover:border-slate-200 dark:hover:border-zinc-700'
                          }`}
                      >
                        <div className="flex items-center gap-3 relative z-10">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0 ${selectedContacts.includes(contact.id)
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : 'border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800'
                            }`}>
                            {selectedContacts.includes(contact.id) && <CheckCircle2 className="w-3 h-3" />}
                          </div>

                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base font-bold shadow-sm transition-colors shrink-0 ${selectedContacts.includes(contact.id) ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400'
                              }`}>
                              {contact.name.charAt(0)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-bold text-slate-800 dark:text-zinc-200 text-[15px]">{contact.name}</h4>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-black uppercase ${contact.category === 'customer'
                                  ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                  : contact.category === 'registered'
                                    ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
                                    : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                  }`}>
                                  {contact.category === 'customer' ? 'عميل' : contact.category === 'registered' ? 'مسجل' : 'جديد'}
                                </span>
                              </div>
                              <p className="text-md font-mono text-slate-500 mt-0.5" dir="ltr">+{contact.number}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 relative z-10">
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold border ${contact.sendStatus === 'sent' ? 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/20' :
                            contact.sendStatus === 'failed' ? 'bg-red-50 text-red-600 border-red-100 dark:bg-red-500/10 dark:border-red-500/20' :
                              'bg-slate-50 text-slate-500 border-slate-100 dark:bg-zinc-800 dark:border-zinc-700'
                            }`}>
                            {contact.sendStatus === 'sent' && <CheckCircle2 className="w-3 h-3" />}
                            {contact.sendStatus === 'failed' && <AlertCircle className="w-3 h-3" />}
                            {contact.sendStatus === 'pending' ? 'في الانتظار' : (contact.sendStatus === 'sent' ? 'تم الإرسال' : 'فشل')}
                          </div>
                          {contact.lastMessage && (
                            <p className="text-[11px] text-slate-400 dark:text-zinc-600 max-w-[150px] truncate hidden sm:block">{contact.lastMessage}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Action Button & Terminal Grid (Sticky at bottom) ── */}
          <div className="sticky bottom-0 z-20 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch mt-2 pt-2 pb-1 bg-gradient-to-t from-white via-white dark:from-zinc-950 dark:via-zinc-950 to-transparent">

            {/* Action Button */}
            <div className="relative group flex">
              <button
                onClick={isSending ? handleStopSending : startBulkSend}
                disabled={!isSending && (connectionStatus !== 'connected' || selectedContacts.length === 0 || (!message.trim() && !media))}
                className={`relative w-full min-h-[140px] rounded-2xl font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border flex flex-col items-center justify-center p-6 ${isSending
                  ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20 hover:bg-red-100 dark:hover:bg-red-500/20'
                  : 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700 hover:border-primary-700 shadow-sm'
                  }`}
              >
                <div className="relative z-20 flex flex-col items-center justify-center gap-3">
                  {isSending ? (
                    <>
                      <div className="w-14 h-14 rounded-xl bg-white/20 dark:bg-red-500/10 flex items-center justify-center mb-1 animate-pulse border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400">
                        <X className="w-7 h-7" strokeWidth={2.5} />
                      </div>
                      <span className="text-2xl tracking-tight">إيقاف الحملة</span>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:-translate-y-1 transition-transform border border-white/20 text-white">
                        <Send className="w-7 h-7" strokeWidth={2} />
                      </div>
                      <span className="text-2xl tracking-wide">بدء الإرسال ({totalSelected})</span>
                    </>
                  )}
                </div>

                {isSending && (
                  <div
                    className="absolute inset-y-0 right-0 bg-black/5 dark:bg-white/5 transition-all duration-300 ease-out z-10"
                    style={{ width: `${sendPct}%` }}
                  />
                )}
              </button>
            </div>

            {/* Terminal Log Console */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-sm flex flex-col overflow-hidden relative" style={{ minHeight: '160px' }}>
              <div className="px-4 py-2.5 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5" dir="ltr">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5 font-mono ml-3">
                    <TerminalSquare className="w-3 h-3 text-slate-400" />
                    logs
                  </span>
                </div>
                {isSending && progress.total > 0 && (
                  <div className="text-[10px] text-emerald-400 font-bold font-mono px-2 py-0.5 bg-emerald-500/10 rounded border border-emerald-500/20">
                    {progress.current}/{progress.total} SENT
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 font-mono text-md custom-scrollbar relative z-10 bg-slate-900/50" dir="ltr">
                {logs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full opacity-40 text-slate-500 font-medium text-md gap-2">
                    Waiting for operations...
                  </div>
                ) : (
                  logs.map((log) => (
                    <div key={log.id} className="flex gap-2 items-start opacity-90 hover:opacity-100 transition-opacity">
                      <span className="text-slate-600 shrink-0">
                        [{log.time.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]
                      </span>
                      <span className={`break-words ${log.type === 'error' ? 'text-red-400 font-semibold' :
                        log.type === 'success' ? 'text-emerald-400 font-semibold' :
                          log.type === 'wait' ? 'text-amber-400' :
                            log.type === 'info' ? 'text-blue-400' :
                              log.type === 'done' ? 'text-slate-300 font-bold uppercase text-md' :
                                'text-slate-400'
                        }`}>
                        <span className="opacity-50 mr-1.5">{'>'}</span>{log.text}
                      </span>
                    </div>
                  ))
                )}
                <div ref={logsEndRef} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
